import {useCallback, useRef} from "react";

import {doc, getDoc} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {firestore} from "~/firebase/firebase";

import {queryClient} from "../queryClient";

export const lazyCallbackFactory =
  <T extends ZodType, CollectionArgs extends NonNullable<unknown>>(
    config: BaseCollectionConfig<T, CollectionArgs>,
  ) =>
  () => {
    const valueCache = useRef<Record<string, T>>({});

    return useCallback((args: CollectionArgs & {id: string}): z.infer<T> => {
      const queryData = queryClient.getQueryData<any>(
        config.getDocumentPath(args),
      );

      // If query data has changed, then update the valueCache
      if (queryData || valueCache.current[args.id]) {
        valueCache.current[args.id] = queryData ?? valueCache.current[args.id];
        return valueCache.current[args.id];
      }

      // If the id isn't in the cache, trigger a lazy load
      queryClient.prefetchQuery({
        queryKey: config.getDocumentPath(args),
        queryFn: async () => {
          const myDoc = await getDoc(
            doc(firestore, ...config.getDocumentPath(args)),
          );
          try {
            return config.parseData(myDoc.id, myDoc.data());
          } catch (err) {
            console.error("Error prefetching document: ", err, myDoc.data());
            throw err;
          }
        },
        staleTime: Number.POSITIVE_INFINITY,
      });

      return config.parseData(args.id, undefined);
    }, []);
  };
